import { useEffect, useState } from "react";
import { Brain, Activity, BarChart3, Shield } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface CrystalBallAnalysisProps {
  onComplete: () => void;
}

const analysisSteps = [
  { icon: Activity, label: "Ingesting sensor data streams...", duration: 1200 },
  { icon: Brain, label: "Running neural pattern recognition...", duration: 1500 },
  { icon: BarChart3, label: "Correlating historical defect patterns...", duration: 1300 },
  { icon: Shield, label: "Generating predictive models...", duration: 1000 },
];

export default function CrystalBallAnalysis({ onComplete }: CrystalBallAnalysisProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let stepTimeout: ReturnType<typeof setTimeout>;
    let progressInterval: ReturnType<typeof setInterval>;

    const totalDuration = analysisSteps.reduce((sum, s) => sum + s.duration, 0);
    let elapsed = 0;

    progressInterval = setInterval(() => {
      elapsed += 50;
      const pct = Math.min((elapsed / totalDuration) * 100, 100);
      setProgress(pct);
    }, 50);

    const runSteps = (index: number) => {
      if (index >= analysisSteps.length) {
        clearInterval(progressInterval);
        setProgress(100);
        setTimeout(onComplete, 400);
        return;
      }
      setCurrentStep(index);
      stepTimeout = setTimeout(() => runSteps(index + 1), analysisSteps[index].duration);
    };

    runSteps(0);

    return () => {
      clearTimeout(stepTimeout);
      clearInterval(progressInterval);
    };
  }, [onComplete]);

  return (
    <section className="relative py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-crystal-blue/20 text-crystal-blue mb-4">
            <span className="text-xl font-bold">2</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            AI Analysis in Progress
          </h2>
        </div>

        <div className="rounded-2xl border border-crystal-purple/20 bg-crystal-card p-8 md:p-12">
          {/* Animated brain icon */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-crystal-purple/30 to-crystal-blue/30 flex items-center justify-center animate-pulse">
                <Brain className="w-12 h-12 text-crystal-purple" />
              </div>
              <div className="absolute inset-0 rounded-full border-2 border-crystal-purple/20 animate-ping" />
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-medium-gray mb-2">
              <span>Processing</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-3 bg-muted [&>div]:bg-gradient-to-r [&>div]:from-crystal-purple [&>div]:to-crystal-blue" />
          </div>

          {/* Steps */}
          <div className="space-y-4">
            {analysisSteps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === currentStep;
              const isDone = index < currentStep;

              return (
                <div
                  key={index}
                  className={`flex items-center gap-4 p-3 rounded-lg transition-all duration-300 ${
                    isActive
                      ? "bg-crystal-purple/10 border border-crystal-purple/30"
                      : isDone
                      ? "opacity-60"
                      : "opacity-30"
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? "text-crystal-purple animate-pulse" : isDone ? "text-crystal-success" : "text-medium-gray"}`} />
                  <span className={`text-sm font-medium ${isActive ? "text-foreground" : "text-medium-gray"}`}>
                    {step.label}
                  </span>
                  {isDone && <span className="ml-auto text-xs text-crystal-success">✓</span>}
                </div>
              );
            })}
          </div>

          <p className="text-center text-xs text-medium-gray mt-6">
            Analyzing 2,847 data points across 14 sensor dimensions...
          </p>
        </div>
      </div>
    </section>
  );
}
